import * as THREE from 'three';

// 3rd-person UFO controls. WASD applies thrust relative to camera heading,
// momentum carries you between inputs, mouse drag orbits camera around UFO.
// `state.camDist` is animated externally (main.js) during focus to "pull back".

const CAM_DIST_DEFAULT = 30;
const CAM_HEIGHT_BIAS = 7;

// Thrust + damping tuned for a hovercraft / saucer feel — gentle ramp,
// long coast after release, capped max speed.
const ACCEL = 95;             // per second
const DAMP_BASE = 0.35;       // higher = more momentum (was 0.06)
const MAX_SPEED = 95;

const FLOOR_Y = 35;
const TILT_MAX = 0.35;        // max banking tilt in radians
const TILT_LAMBDA = 5;        // how fast tilt approaches target

export function createControls(canvas, getDisabled) {
  const state = {
    yaw: 0,
    pitch: -0.22,
    ufoPos: new THREE.Vector3(0, 200, 380),
    velocity: new THREE.Vector3(),
    camDist: CAM_DIST_DEFAULT,
    keys: {},
    isDragging: false,
    lastMouse: { x: 0, y: 0 },
    pressedAt: null,
    onClickIsland: null,
    locked: false
  };

  window.addEventListener('keydown', e => {
    state.keys[e.key.toLowerCase()] = true;
  });
  window.addEventListener('keyup', e => {
    state.keys[e.key.toLowerCase()] = false;
  });

  canvas.addEventListener('pointerdown', e => {
    state.isDragging = true;
    state.lastMouse = { x: e.clientX, y: e.clientY };
    state.pressedAt = { x: e.clientX, y: e.clientY };
    canvas.setPointerCapture(e.pointerId);
  });

  canvas.addEventListener('pointermove', e => {
    if (!state.isDragging) return;
    const dx = e.clientX - state.lastMouse.x;
    const dy = e.clientY - state.lastMouse.y;
    state.lastMouse = { x: e.clientX, y: e.clientY };
    state.yaw -= dx * 0.004;
    state.pitch -= dy * 0.0032;
    state.pitch = Math.max(-Math.PI / 2.4, Math.min(0.25, state.pitch));
  });

  canvas.addEventListener('pointerup', e => {
    const drag = state.pressedAt ? Math.hypot(e.clientX - state.pressedAt.x, e.clientY - state.pressedAt.y) : 999;
    state.isDragging = false;
    try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
    state.pressedAt = null;
    if (drag < 5 && state.onClickIsland) {
      state.onClickIsland(e.clientX, e.clientY);
    }
  });

  function getForward() {
    return new THREE.Vector3(
      -Math.sin(state.yaw) * Math.cos(state.pitch),
       Math.sin(state.pitch),
      -Math.cos(state.yaw) * Math.cos(state.pitch)
    );
  }
  function getForwardH() {
    return new THREE.Vector3(-Math.sin(state.yaw), 0, -Math.cos(state.yaw));
  }
  // FIXED: right of forward(-sin yaw, 0, -cos yaw) is (cos yaw, 0, -sin yaw).
  // The previous version had this negated, which is why A/D felt swapped.
  function getRightH() {
    return new THREE.Vector3(Math.cos(state.yaw), 0, -Math.sin(state.yaw));
  }

  function placeCamera(camera) {
    const f = getForward();
    const offset = f.clone().multiplyScalar(-state.camDist);
    offset.y += CAM_HEIGHT_BIAS;
    camera.position.copy(state.ufoPos).add(offset);
    camera.lookAt(state.ufoPos);
  }

  function applyTilt(ufo, dt) {
    // UFO is a symmetric disc that never rotates around Y, so tilt directly in
    // world axes: whichever world direction velocity points, dip that side.
    //  rotation.x  → tilts +Z down when positive, -Z down when negative
    //  rotation.z  → tilts -X down when positive, +X down when negative
    // Want the side IN the motion direction to dip → match signs accordingly.
    const TILT_FACTOR = 0.008;
    const tiltX = THREE.MathUtils.clamp( state.velocity.z * TILT_FACTOR, -TILT_MAX, TILT_MAX);
    const tiltZ = THREE.MathUtils.clamp(-state.velocity.x * TILT_FACTOR, -TILT_MAX, TILT_MAX);
    ufo.rotation.x = THREE.MathUtils.damp(ufo.rotation.x, tiltX, TILT_LAMBDA, dt);
    ufo.rotation.z = THREE.MathUtils.damp(ufo.rotation.z, tiltZ, TILT_LAMBDA, dt);
  }

  function update(dt, camera, ufo) {
    const bob = Math.sin(performance.now() * 0.002) * 0.35;

    if (state.locked || (getDisabled && getDisabled())) {
      // Hovering in place — apply gentle damping to any residual velocity
      state.velocity.multiplyScalar(Math.pow(0.1, dt));
      if (ufo) {
        ufo.position.copy(state.ufoPos);
        ufo.position.y += bob;
        // Settle tilt back to level
        ufo.rotation.x = THREE.MathUtils.damp(ufo.rotation.x, 0, 4, dt);
        ufo.rotation.z = THREE.MathUtils.damp(ufo.rotation.z, 0, 4, dt);
      }
      placeCamera(camera);
      return;
    }

    const fh = getForwardH();
    const rh = getRightH();
    const up = new THREE.Vector3(0, 1, 0);
    const k = state.keys;
    if (k['w'] || k['arrowup'])    state.velocity.addScaledVector(fh,  ACCEL * dt);
    if (k['s'] || k['arrowdown'])  state.velocity.addScaledVector(fh, -ACCEL * dt);
    if (k['a'] || k['arrowleft'])  state.velocity.addScaledVector(rh, -ACCEL * dt);
    if (k['d'] || k['arrowright']) state.velocity.addScaledVector(rh,  ACCEL * dt);
    if (k[' '])                    state.velocity.addScaledVector(up,  ACCEL * dt);
    if (k['shift'])                state.velocity.addScaledVector(up, -ACCEL * dt);

    // Gentle continuous damping (keeps momentum for ~2-3 seconds after release)
    state.velocity.multiplyScalar(Math.pow(DAMP_BASE, dt));

    // Speed cap
    const speed = state.velocity.length();
    if (speed > MAX_SPEED) state.velocity.multiplyScalar(MAX_SPEED / speed);

    state.ufoPos.addScaledVector(state.velocity, dt);
    if (state.ufoPos.y < FLOOR_Y) state.ufoPos.y = FLOOR_Y;

    if (ufo) {
      ufo.position.copy(state.ufoPos);
      ufo.position.y += bob;
      applyTilt(ufo, dt);
    }
    placeCamera(camera);
  }

  return { state, update, CAM_DIST_DEFAULT };
}
